/**
 * Servicio para la gestión de documentos de empleados
 * Fecha: 16 Oct 2025
 */

import { apiRequest, API_URL, getAuthToken } from '@/shared/lib/apiUtils';
import type {
  EmployeeDocument,
  DocumentSummary,
  EmployeeDocumentFilters,
  UploadEmployeeDocumentDto,
} from '@/types/employee.types';

/**
 * Servicio de documentos de empleados con todos los endpoints disponibles
 */
export const employeeDocumentsService = {
  /**
   * Listar documentos con filtros opcionales
   */
  getAll: async (filters?: EmployeeDocumentFilters): Promise<EmployeeDocument[]> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    const queryString = params.toString();
    const url = queryString
      ? `${API_URL}/employee-documents?${queryString}`
      : `${API_URL}/employee-documents`;
    return apiRequest<EmployeeDocument[]>(url);
  },

  /**
   * Obtener resumen de documentos de un empleado
   */
  getSummary: async (employeeId: number): Promise<DocumentSummary> => {
    return apiRequest<DocumentSummary>(
      `${API_URL}/employee-documents/employee/${employeeId}/summary`
    );
  },

  /**
   * Obtener todas las versiones de un documento
   */
  getVersions: async (
    employeeId: number,
    documentTypeId: number
  ): Promise<EmployeeDocument[]> => {
    return apiRequest<EmployeeDocument[]>(
      `${API_URL}/employee-documents/employee/${employeeId}/type/${documentTypeId}/versions`
    );
  },

  /**
   * Obtener documento por ID
   */
  getById: async (id: number): Promise<EmployeeDocument> => {
    return apiRequest<EmployeeDocument>(`${API_URL}/employee-documents/${id}`);
  },

  /**
   * Subir documento con archivo
   * Los IDs se envían como strings en FormData para que el backend los reciba correctamente
   */
  upload: async (
    file: File,
    data: UploadEmployeeDocumentDto
  ): Promise<EmployeeDocument> => {
    const token = getAuthToken();
    
    const url = `${API_URL}/employee-documents/upload`;
    
    // FormData con todos los campos necesarios
    const formData = new FormData();
    formData.append('file', file);
    formData.append('employeeId', data.employeeId.toString());
    formData.append('documentTypeId', data.documentTypeId.toString());
    if (data.comentarios) {
      formData.append('comentarios', data.comentarios);
    }
    if (data.fechaVencimiento) {
      formData.append('fechaVencimiento', data.fechaVencimiento);
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw {
        message: errorData.message || 'Error al subir documento',
        error: errorData.error || 'Error desconocido',
        statusCode: response.status,
      };
    }

    return await response.json();
  },

  /**
   * Crear documento sin archivo (URL externa)
   */
  create: async (data: {
    employeeId: number;
    documentTypeId: number;
    fileUrl: string;
    fileName?: string;
    comentarios?: string;
  }): Promise<EmployeeDocument> => {
    return apiRequest<EmployeeDocument>(`${API_URL}/employee-documents`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Actualizar metadatos del documento
   */
  update: async (
    id: number,
    data: Partial<EmployeeDocument>
  ): Promise<EmployeeDocument> => {
    return apiRequest<EmployeeDocument>(`${API_URL}/employee-documents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * Aprobar documento
   */
  approve: async (id: number, comentarios: string): Promise<EmployeeDocument> => {
    return apiRequest<EmployeeDocument>(`${API_URL}/employee-documents/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({
        status: 'aprobado',
        comentarios,
      }),
    });
  },

  /**
   * Rechazar documento
   */
  reject: async (id: number, comentarios: string): Promise<EmployeeDocument> => {
    return apiRequest<EmployeeDocument>(`${API_URL}/employee-documents/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({
        status: 'rechazado',
        comentarios,
      }),
    });
  },

  /**
   * Marcar documento en revisión
   */
  setInReview: async (id: number): Promise<EmployeeDocument> => {
    return apiRequest<EmployeeDocument>(`${API_URL}/employee-documents/${id}/set-in-review`, {
      method: 'POST',
    });
  },

  /**
   * Enviar documento a revisión con comentarios
   */
  sendToReview: async (id: number, comentarios?: string): Promise<EmployeeDocument> => {
    return apiRequest<EmployeeDocument>(`${API_URL}/employee-documents/${id}/send-to-review`, {
      method: 'POST',
      body: JSON.stringify({
        status: 'en_revision',
        comentarios,
      }),
    });
  },

  /**
   * Marcar documento como vencido
   */
  markAsExpired: async (id: number, comentarios?: string): Promise<EmployeeDocument> => {
    return apiRequest<EmployeeDocument>(`${API_URL}/employee-documents/${id}/mark-expired`, {
      method: 'POST',
      body: JSON.stringify({
        status: 'vencido',
        comentarios: comentarios || 'Documento marcado como vencido manualmente',
      }),
    });
  },

  /**
   * Verificar documentos vencidos
   */
  checkExpired: async (): Promise<EmployeeDocument[]> => {
    return apiRequest<EmployeeDocument[]>(`${API_URL}/employee-documents/check-expired`, {
      method: 'POST',
    });
  },

  /**
   * Eliminar documento (solo pendientes/rechazados)
   */
  delete: async (id: number): Promise<void> => {
    await apiRequest<void>(`${API_URL}/employee-documents/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Marcar documento como "No Aplica"
   * Para documentos que no son requeridos para ciertos empleados
   */
  markAsNotApplicable: async (
    employeeId: number,
    documentTypeId: number,
    comentarios?: string
  ): Promise<EmployeeDocument> => {
    return apiRequest<EmployeeDocument>(`${API_URL}/employee-documents/not-applicable`, {
      method: 'POST',
      body: JSON.stringify({
        employeeId,
        documentTypeId,
        comentarios: comentarios || 'Este documento no aplica para el empleado',
      }),
    });
  },
};
